import type { BoardData, Channel, Item, StandBase, StandSpec, StandType } from '../model/schema.js';
import { fieldColumns } from '../derive/columns.js';
import { formatLength } from '../units/units.js';
import type { Units } from '../units/units.js';
import { holderLabel, holderSetting } from './holder.js';

const TYPE_LABEL: Record<'en' | 'de', Record<StandType, string>> = {
  en: {
    straight: 'Straight stand',
    boom: 'Boom stand',
    telescopic_boom: 'Telescopic boom',
    short_boom: 'Short boom',
    desk: 'Desk stand',
    clamp: 'Clamp',
    overhead: 'Overhead stand',
    speaker_stand: 'Speaker stand',
    lighting_stand: 'Lighting stand',
    truss_lift: 'Truss lift',
  },
  de: {
    straight: 'Gerades Stativ',
    boom: 'Galgenstativ',
    telescopic_boom: 'Teleskop-Galgen',
    short_boom: 'Kurzer Galgen',
    desk: 'Tischstativ',
    clamp: 'Klemme',
    overhead: 'Overhead-Stativ',
    speaker_stand: 'Boxenstativ',
    lighting_stand: 'Lichtstativ',
    truss_lift: 'Traversenlift',
  },
};

const BASE_LABEL: Record<'en' | 'de', Record<StandBase, string>> = {
  en: {
    tripod: 'tripod',
    low_profile_tripod: 'low-profile tripod',
    round: 'round base',
    weighted: 'weighted base',
    clamp: 'clamp mount',
  },
  de: {
    tripod: 'Dreibein',
    low_profile_tripod: 'flaches Dreibein',
    round: 'Rundfuß',
    weighted: 'Schwerer Fuß',
    clamp: 'Klemmhalterung',
  },
};

/** "1.5" / German "1,5" (OIL-157). */
function num(v: number, locale: 'en' | 'de'): string {
  const text = String(Number(v.toFixed(2)));
  return locale === 'de' ? text.replace('.', ',') : text;
}

function metres(v: number, locale: 'en' | 'de'): string {
  return `${num(v, locale)} m`;
}

/**
 * Human description, e.g. "Telescopic boom · tripod · 0.9–1.6 m · boom 0.8 m"; on an imperial
 * board "… · 2'11"–5'3" · boom 2'7"" (OIL-249).
 */
export function describeStand(
  spec: StandSpec,
  locale: 'en' | 'de' = 'en',
  units: Units = 'metric',
): string {
  const len = (v: number) => (units === 'imperial' ? formatLength(v, units) : metres(v, locale));
  const parts = [TYPE_LABEL[locale][spec.type]];
  if (spec.base !== undefined) parts.push(BASE_LABEL[locale][spec.base]);
  if (spec.minHeight !== undefined && spec.maxHeight !== undefined) {
    parts.push(
      units === 'imperial'
        ? `${len(spec.minHeight)}–${len(spec.maxHeight)}`
        : `${num(spec.minHeight, locale)}–${metres(spec.maxHeight, locale)}`,
    );
  } else if (spec.maxHeight !== undefined) {
    parts.push(`≤ ${len(spec.maxHeight)}`);
  } else if (spec.minHeight !== undefined) {
    parts.push(`≥ ${len(spec.minHeight)}`);
  }
  if (spec.boomLength !== undefined) {
    parts.push(`${locale === 'de' ? 'Galgen' : 'boom'} ${len(spec.boomLength)}`);
  }
  const holder = holderSetting(spec);
  if (holder !== 'auto' && holder !== 'none')
    parts.push(
      locale === 'de'
        ? `mit ${holderLabel(holder, locale)}`
        : `with ${holderLabel(holder, locale).toLowerCase()}`,
    );
  return parts.join(' · ');
}

/** The stand an item is mounted on, if that reference resolves to a stand. */
export function standOf(data: BoardData, item: Item | undefined): Item | undefined {
  const stand = item?.mountedOn === undefined ? undefined : data.items[item.mountedOn];
  return stand?.kind === 'stand' ? stand : undefined;
}

/** Short stand text for lists: the formal spec, else the stand item's label. */
export function standText(
  stand: Item,
  locale: 'en' | 'de' = 'en',
  units: Units = 'metric',
): string {
  if (stand.stand !== undefined) return describeStand(stand.stand, locale, units);
  // a stand without type and label is no blank row (OIL-132)
  return stand.label.trim() || (locale === 'de' ? 'Ohne Typ' : 'No type');
}

export interface StandListRow {
  rowKey: string;
  description: string;
  type: string;
  base: string;
  count: number;
  /** Labels of the mics/DIs mounted on stands of this kind (informal rows: the channels). */
  usedBy: string;
  /** True for rows aggregated from informal `Channel.stand` texts (no stand item yet). */
  informal: boolean;
}

/** Quote characters stripped from informal stand texts („X-High“, "Boom", 'Clip' …). */
const QUOTES = /^[\s"'„“”‚‘’«»‹›]+|[\s"'„“”‚‘’«»‹›]+$/gu;

/** Informal stand text as listed: trimmed, without surrounding quotes, single spaces. */
export function normalizeStandText(text: string): string {
  return text.replace(QUOTES, '').replace(/\s+/g, ' ');
}

/** "Ch 13 Piano Hole" – how a channel is named in derived lists. */
export function channelLabel(channel: Pick<Channel, 'number' | 'name'>): string {
  return `Ch ${channel.number} ${channel.name}`.trim();
}

/**
 * Informal stand requirements: `Channel.stand` texts of channels whose source item is not
 * mounted on a stand item, grouped case-insensitively by their normalised text.
 */
function informalStandRows(data: BoardData): StandListRow[] {
  const groups = new Map<string, StandListRow & { users: Channel[] }>();
  const channels = Object.values(data.channels).sort((a, b) => a.number - b.number);
  for (const ch of channels) {
    const text = normalizeStandText(ch.stand ?? '');
    const source = ch.sourceItemId === undefined ? undefined : data.items[ch.sourceItemId];
    if (text === '' || standOf(data, source) !== undefined) continue;
    const key = text.toLowerCase();
    const row = groups.get(key) ?? {
      rowKey: `informal:${key}`,
      description: text,
      type: '',
      base: '',
      count: 0,
      usedBy: '',
      informal: true,
      users: [],
    };
    row.count += 1;
    row.users.push(ch);
    groups.set(key, row);
  }
  return [...groups.values()]
    .map(({ users, ...row }) => ({ ...row, usedBy: users.map(channelLabel).join(', ') }))
    .sort((a, b) => b.count - a.count || a.description.localeCompare(b.description));
}

/**
 * Stand requirements for the rider (decision U1): all stand items grouped by identical
 * description, with counts and what is mounted on them, sorted by count, then description.
 * Informal channel stand texts (input list column "Stand" without a stand item) follow as rows
 * with `informal: true`, so an imported list is summarised before objects exist.
 */
export function standList(data: BoardData, locale: 'en' | 'de' = 'en'): StandListRow[] {
  const items = Object.values(data.items);
  const groups = new Map<string, StandListRow & { users: string[] }>();
  for (const stand of items.filter((i) => i.kind === 'stand')) {
    const description = standText(stand, locale, data.meta.units);
    const row = groups.get(description) ?? {
      rowKey: `stand:${description}`,
      description,
      type: stand.stand?.type ?? '',
      base: stand.stand?.base ?? '',
      count: 0,
      usedBy: '',
      informal: false,
      users: [],
    };
    row.count += 1;
    row.users.push(...items.filter((i) => i.mountedOn === stand.id).map((i) => i.label));
    groups.set(description, row);
  }
  const formal = [...groups.values()]
    .map(({ users, ...row }) => ({ ...row, usedBy: users.sort().join(', ') }))
    .sort((a, b) => b.count - a.count || a.description.localeCompare(b.description));
  return [...formal, ...informalStandRows(data)];
}

export const STAND_LIST_COLUMNS = fieldColumns<StandListRow>('stands', [
  'count',
  'description',
  'usedBy',
]);
